import { getOverview, OverviewInfo, ExtendedOverviewInfo } from "@ugg/shared/api/data-parser/champions/overview";
import { Multibuilds, KaynFormIds } from "@ugg/shared/interfaces/champions/overview.interface";
import { comparePatch } from "@ugg/shared/utils/patch-helpers";
import { normalizeChampionUrlAlias } from "@ugg/shared/utils/champion-alias";

export const multibuildTransformMap: { [key in Multibuilds]?: number } = {
  [Multibuilds.REDKAYN]: KaynFormIds.RED,
  [Multibuilds.BLUEKAYN]: KaynFormIds.BLUE,
};

export const champion_transform_map: { [key: number]: number[] } = {
  141: [KaynFormIds.RED, KaynFormIds.BLUE],
};

export function getDefaultChampionTransformId(championId: number) {
  return champion_transform_map[championId] ? champion_transform_map[championId][0] : championId;
}

// League Classic champion ids live in a dedicated high range (60000–69999).
// These are REAL champions, not multi-build "transforms" (e.g. Red/Blue Kayn,
// which are encoded as 100141/200141). Historically any id > 10000 was assumed
// to be a transform, which incorrectly swept up the Classic range — so transform
// logic has to explicitly exclude it.
export const CLASSIC_CHAMPION_ID_MIN = 60000;
export const CLASSIC_CHAMPION_ID_MAX = 70000; // exclusive upper bound

export function isClassicChampion(championId: number) {
  return championId >= CLASSIC_CHAMPION_ID_MIN && championId < CLASSIC_CHAMPION_ID_MAX;
}

// Recovers the base riot champion id from any encoded variant:
//   60103 (Classic Ahri)  → 103
//   100141 (Red Kayn)     → 141
//   103                   → 103
//
// The `60000 +` offset is Riot's own convention, not ours — DDragon ships
// Classic champions as `Jade_Ahri` with key "60103". Needed wherever an id
// arrives from an external payload (live game, champ select) and has to be
// resolved to a name/alias: riot-assets would map 60103 to "jade_ahri", which
// is not a valid build-page alias.
export function getBaseChampionId(championId: number) {
  if (isClassicChampion(championId)) return championId - CLASSIC_CHAMPION_ID_MIN;
  return isChampionTransform(championId) ? championId % 10000 : championId;
}

// URL aliases (riot id-name, lowercased — the value carried in the
// /lol/champions/:champion route) of the champions that have a League Classic
// counterpart. Hardcoded ON PURPOSE so the normal champion build page can decide
// whether to show the "Classic" tab with ZERO fetches: the alias is already in the
// route param, whereas resolving it to a numeric champion id needs riot's async
// championMini (which returns 0 until it loads, so the tab would flicker in only
// after hydration and never render at SSR). Keying by alias sidesteps that lookup.
//
// This is the fixed launch roster (~60; the Classic id is `60000 + baseId`, e.g.
// Ahri 103 → 60103). If an alias is ever missed or misspelled, the modern page
// simply omits the tab — no broken link. Replace with a manifest/DDragon-driven
// check once League Classic joins the standard riot-assets pipeline.
export const CLASSIC_COUNTERPART_ALIASES = new Set<string>([
  "annie",
  "olaf",
  "twistedfate",
  "fiddlesticks",
  "kayle",
  "masteryi",
  "alistar",
  "ryze",
  "sion",
  "sivir",
  "soraka",
  "teemo",
  "tristana",
  "warwick",
  "nunu",
  "missfortune",
  "ashe",
  "tryndamere",
  "jax",
  "morgana",
  "zilean",
  "singed",
  "evelynn",
  "twitch",
  "karthus",
  "chogath",
  "amumu",
  "rammus",
  "anivia",
  "shaco",
  "drmundo",
  "sona",
  "kassadin",
  "janna",
  "gangplank",
  "corki",
  "taric",
  "veigar",
  "blitzcrank",
  "malphite",
  "katarina",
  "jarvaniv",
  // Riot id "MonkeyKing"; "wukong" is the canonical u.gg URL alias and the key
  // the Classic manifest uses. The non-canonical spelling is handled by
  // normalizeChampionUrlAlias below rather than by a second entry here — listing
  // it made the tab appear on /lol/champions/monkeyking/build while the link it
  // produced ("monkeyking-classic") resolved to nothing.
  "wukong",
  "brand",
  "leesin",
  "vayne",
  "skarner",
  "heimerdinger",
  "nasus",
  "nidalee",
  "gragas",
  "pantheon",
  "ezreal",
  "garen",
  "leona",
  "malzahar",
  "kogmaw",
  "lux",
  "ahri",
  "lulu",
]);

// Champions whose Classic counterpart ships in a FUTURE patch, keyed by the
// patch that activates them. This exists so a roster addition can be merged and
// deployed AHEAD of the game patch: entries here are inert until u.gg's live
// patch reaches the key, so shipping them early can't surface a Classic tab for
// a champion whose Classic page has no data yet.
//
//   { "16_16": ["akali", "kennen", "shen"] }
//
// Values are URL aliases, same space as CLASSIC_COUNTERPART_ALIASES (lowercased
// riot id-name). Add a new key per release and list the patches in ascending
// order — an alias listed under two patches keeps the first occurrence, which is
// the earliest one only if the map is authored in order. Duplicates are a
// data-entry mistake, not a supported case.
//
// Activation compares against the LIVE patch (newest from useUGGPatches), not
// the patch a user has selected in a filter — activation is a global switch that
// flips on rollover, not a per-view question. A champion added in 16_16 keeps its
// tab while browsing 16_15 data.
//
// Once a patch here has gone live and stayed live, its aliases can be folded
// into CLASSIC_COUNTERPART_ALIASES and the key deleted — that also makes them
// visible to callers that pass no patch (see hasClassicCounterpart).
export const CLASSIC_COUNTERPART_ALIASES_BY_PATCH: Record<string, string[]> = {
  "16_16": ["akali", "kennen", "shen"],
};

// Flattened alias → activation patch, built once at module load so the check
// below stays an O(1) lookup instead of scanning every patch key on every call
// (hasClassicCounterpart runs on each champion page render and, via
// getChampionBuildUrlForQueue, once per champion card in a live game).
//
// No comparePatch here on purpose: calling into another module during module
// evaluation is the kind of thing that breaks on an import-order change. The
// numeric comparison happens at call time.
const CLASSIC_COUNTERPART_ACTIVATION_PATCH: Map<string, string> = (() => {
  const map = new Map<string, string>();
  for (const [patch, aliases] of Object.entries(CLASSIC_COUNTERPART_ALIASES_BY_PATCH)) {
    for (const alias of aliases) {
      const key = String(alias).toLowerCase();
      if (!map.has(key)) map.set(key, patch);
    }
  }
  return map;
})();

// Whether a champion (by URL alias) has a League Classic counterpart. Alias-based
// so it needs no riot-assets lookup — see CLASSIC_COUNTERPART_ALIASES.
//
// `patch` is the live u.gg patch (wire format, "16_16"). It only matters for the
// pre-deployed additions above; the core roster never consults it, so callers
// that have no patch to hand keep exactly the zero-fetch, SSR-stable behavior
// this function was written for. Omitting it under-reports rather than
// over-reports — a not-yet-known counterpart just means no Classic tab or link,
// which is the same harmless outcome as a missing alias, whereas over-reporting
// would link to a Classic page with no data.
export function hasClassicCounterpart(championAlias: string | undefined | null, patch?: string): boolean {
  if (championAlias == null) return false;
  // Normalized, not just lowercased: the roster and the Classic manifest are both
  // keyed on the canonical alias, but callers hand us a route param that may be a
  // non-canonical spelling the modern build page also serves ("monkeyking").
  // Matching on the raw value would either miss the tab or — worse, if the
  // spelling were listed here — show a tab whose Classic URL resolves to nothing.
  const alias = normalizeChampionUrlAlias(championAlias);
  if (CLASSIC_COUNTERPART_ALIASES.has(alias)) return true;

  const activationPatch = CLASSIC_COUNTERPART_ACTIVATION_PATCH.get(alias);
  if (!activationPatch || !patch) return false;
  // Season/week-numeric, not lexicographic — "16_9" >= "16_15" is true as
  // strings and would activate a whole patch early.
  return comparePatch(patch, activationPatch) >= 0;
}

export function isChampionTransform(championId: number) {
  if (isClassicChampion(championId)) return false;
  return championId > 10000;
}

// Recovers the base champion id from a transform id (e.g. 100141 -> 141).
// Non-transform ids — including Classic champions — are returned unchanged, so
// e.g. 65000 stays 65000 instead of collapsing to 5000 via `% 10000`.
export function untransformChampion(championId: number) {
  return isChampionTransform(championId) ? championId % 10000 : championId;
}

export function getChampionOverview(
  championId: number,
  championOverviews: { [key: number]: { [key: string]: OverviewInfo } },
  selector: string,
) {
  return championOverviews?.[championId]?.[selector];
}

export function convertObjectToArray(obj: TabData) {
  const arr = Object.entries(obj);
  arr.sort((a, b) => {
    if (a[0] === Multibuilds.RECOMMENDED) return -1;
    return b[1].matches - a[1].matches;
  });
  return arr;
}

export function capitalizeMulti(multi: string, fallback = "Recommended") {
  switch (multi) {
    case Multibuilds.AP:
    case Multibuilds.AD:
      return multi.toUpperCase();
    case Multibuilds.TANK:
    case Multibuilds.CRIT:
    case Multibuilds.LETHALITY:
      return multi.substring(0, 1).toUpperCase() + multi.substring(1);
    case Multibuilds.ONHIT:
      return "On Hit";
    case Multibuilds.REDKAYN:
      return "Red";
    case Multibuilds.BLUEKAYN:
      return "Blue";
    default:
      return fallback;
  }
}

export function shouldEnableBuild(filteredOverview: OverviewInfo, filteredTargetOverview: OverviewInfo, bootItems: number[]) {
  const defaultKeyItem = filteredOverview?.rec_core_items?.ids
    ? getClassifyItem(filteredOverview?.rec_core_items?.ids, bootItems)
    : 0;
  const targetKeyItem = filteredTargetOverview?.rec_core_items?.ids
    ? getClassifyItem(filteredTargetOverview?.rec_core_items?.ids, bootItems)
    : 0;
  const shouldEnableType =
    filteredTargetOverview &&
    defaultKeyItem &&
    targetKeyItem &&
    !(targetKeyItem === defaultKeyItem) &&
    filteredTargetOverview?.item_options_1 &&
    filteredTargetOverview.item_options_1.length > 1 &&
    filteredTargetOverview?.item_options_2 &&
    filteredTargetOverview.item_options_2.length > 1 &&
    filteredTargetOverview?.item_options_3 &&
    filteredTargetOverview.item_options_3.length > 1 &&
    (filteredTargetOverview?.rec_starting_items?.matches ?? 0) > 9 &&
    (filteredTargetOverview?.rec_core_items?.matches ?? 0) > 9;
  return shouldEnableType;
}

export function getClassifyItem(items: number[], bootItems: number[] | undefined) {
  if (!items.length || !bootItems || !bootItems.length) {
    return 0;
  }
  let normal = 0;
  let others = 0;
  for (let i = 0; i < items.length; i++) {
    if (!bootItems.includes(items[i]) && normal === 0) {
      normal = items[i];
    } else {
      others = items[i];
    }
  }
  return normal !== 0 ? normal : others;
}

export interface MultipleChampionOverviewData {
  forceDefaultData: boolean;
  championOverview: Record<string, OverviewInfo>;
  apChampionOverview: Record<string, OverviewInfo>;
  tankChampionOverview: Record<string, OverviewInfo>;
  adChampionOverview: Record<string, OverviewInfo>;
  critChampionOverview: Record<string, OverviewInfo>;
  lethalityChampionOverview: Record<string, OverviewInfo>;
  onHitChampionOverview: Record<string, OverviewInfo>;
  fetchingChampionOverview: boolean;
  errorChampionOverview: Error | null;
}

export function cleanMultipleOverviewData(generatedOverviews: ExtendedOverviewInfo[] | null, bootItems: number[]) {
  if (!generatedOverviews || !generatedOverviews[0]) {
    return null;
  }

  const defaultOverview = generatedOverviews[0];
  const defaultType = generatedOverviews[0].type;
  const defaultKeyItem = defaultOverview?.rec_core_items?.ids
    ? getClassifyItem(defaultOverview?.rec_core_items?.ids, bootItems)
    : 0;

  return generatedOverviews
    .filter((val) => {
      const enoughData =
        (val?.item_options_1?.length ?? 0) > 1 &&
        (val?.item_options_2?.length ?? 0) > 1 &&
        (val?.item_options_3?.length ?? 0) > 1 &&
        (val?.rec_starting_items?.matches ?? 0) > 9 &&
        (val?.rec_core_items?.matches ?? 0) > 9;
      const isMatchingDefault =
        val.type !== defaultType ? getClassifyItem(val?.rec_core_items?.ids ?? [], bootItems) !== defaultKeyItem : true;

      return isChampionTransform(Number(val.type)) || val.type === defaultType ? true : enoughData && isMatchingDefault;
    })
    .sort((a, b) => {
      if (isChampionTransform(Number(a.type))) {
        return Number(a.type) - Number(b.type); // Sort by champion id
      }
      if (a.type === Multibuilds.RECOMMENDED) return -1; // Recommended should always be first

      return b.matches - a.matches;
    })
    .slice(0, 4);
}

export function parseCleanMultipleOverviewData(
  data: ExtendedOverviewInfo[],
  transformChampionId: number,
  multi: string | undefined,
) {
  return data.find(({ type }) => {
    return Number(type) === transformChampionId || type === multi;
  });
}

export function generateMultipleOverviews(
  data: Record<number, MultipleChampionOverviewData>,
  championId: number,
  fullParams: {
    queueType: string;
    region: string;
    rank: string;
    role: string;
  },
) {
  const transformChampionId = getDefaultChampionTransformId(championId);
  const isTransformedChamp = isChampionTransform(transformChampionId);
  const isLoading = isTransformedChamp
    ? champion_transform_map[championId]
        .map((val: number) => {
          return data[val]["fetchingChampionOverview"];
        })
        .some((val) => val)
    : data[championId]["fetchingChampionOverview"];

  if (isLoading) {
    return null;
  }

  let overviewObjs: Array<[OverviewInfo | null, string]> = isTransformedChamp
    ? champion_transform_map[championId].map((val: number) => {
        return [getOverview(data[val]["championOverview"], fullParams), val + ""];
      })
    : [
        [getOverview(data[championId]["championOverview"], fullParams), Multibuilds.RECOMMENDED],
        [getOverview(data[championId]["apChampionOverview"], fullParams), Multibuilds.AP],
        [getOverview(data[championId]["tankChampionOverview"], fullParams), Multibuilds.TANK],
        [getOverview(data[championId]["adChampionOverview"], fullParams), Multibuilds.AD],
        [getOverview(data[championId]["critChampionOverview"], fullParams), Multibuilds.CRIT],
        [getOverview(data[championId]["lethalityChampionOverview"], fullParams), Multibuilds.LETHALITY],
        [getOverview(data[championId]["onHitChampionOverview"], fullParams), Multibuilds.ONHIT],
      ];

  let cleanedOverviewObjs = overviewObjs.filter((val) => {
    return val[0] ? true : false;
  }) as Array<[OverviewInfo, string]>;

  return cleanedOverviewObjs.map((val) => {
    return { ...val[0], type: val[1] };
  });
}

export type TabData = Record<string, { keystone: number | undefined; item: number; matches: number }>;
export function generateTabs(cleanedMultipleOverviews: ExtendedOverviewInfo[] | null, bootItems: number[]) {
  const tabData: TabData = {};
  if (cleanedMultipleOverviews) {
    cleanedMultipleOverviews.forEach((val) => {
      if (val) {
        tabData[val.type] = {
          keystone: val?.rec_runes?.active_perks[0],
          item: val?.rec_core_items?.ids ? getClassifyItem(val?.rec_core_items?.ids, bootItems) : 0,
          matches: val.matches ? val.matches : 0,
        };
      }
    });
  }

  return tabData;
}

export function parseChampionOverview(
  championId: number,
  championOverviews: Record<number, MultipleChampionOverviewData>,
  multi: string,
) {
  if (Object.keys(championOverviews).length === 0) {
    return null;
  } else if (isChampionTransform(championId) || !multi || multi === Multibuilds.RECOMMENDED) {
    return championOverviews[championId]["championOverview"];
  }
  const overviewKey = overviewKeyObjMap[multi];
  return overviewKey ? championOverviews[championId][overviewKey] : null;
}

const overviewKeyObjMap: {
  [key: string]: keyof Omit<
    MultipleChampionOverviewData,
    "forceDefaultData" | "fetchingChampionOverview" | "errorChampionOverview"
  >;
} = {
  [Multibuilds.RECOMMENDED]: "championOverview",
  [Multibuilds.AD]: "adChampionOverview",
  [Multibuilds.AP]: "apChampionOverview",
  [Multibuilds.TANK]: "tankChampionOverview",
  [Multibuilds.CRIT]: "critChampionOverview",
  [Multibuilds.LETHALITY]: "lethalityChampionOverview",
  [Multibuilds.ONHIT]: "onHitChampionOverview",
};
